import { db } from '../config/firebase';
import {
  collection, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { swissPairing, roundRobinSchedule, knockoutBracket, knockoutNextRound } from './pairing';
import { ratingOf, categoryInfo } from '../config/ratingCategories';
import { saveEvent, addGame, periodKeyOf, liveProjection, fetchPlayer } from './ifrData';

// ══════════════════════════════════════════════════════════════════════
//  TUKIO LA PAPO HAPO — kusimamia mashindano ya IFR moja kwa moja hadi
//  mshindi, kisha kuyahamishia kwenye mfumo rasmi wa rating.
//
//  Hii ni SCRATCHPAD ya muda: haiguswi na ratedEvents/ratedGames mpaka
//  admin abofye "Maliza na Uhamishe kwenye IFR". Ukiacha tukio katikati
//  bila kulimaliza, hakuna athari yoyote kwa rating rasmi za mtu yeyote.
//
//  MUUNDO WA "DRAFT KWANZA" (muhimu kwa mashindano makubwa, mf. wanafunzi
//  400): tukio linapotengenezwa, HUREKODIWI FIRESTORE MARA MOJA (status
//  "draft") — KABLA ya mchezaji hata mmoja kuongezwa. Kila mchezaji
//  anayeongezwa anahifadhiwa MARA MOJA (operesheni yake ya updateDoc
//  peke yake). Hivyo, mtandao ukikatika, kivinjari kikifungwa kwa
//  bahati mbaya, au mtu mwingine akiendelea baadaye kwenye kifaa
//  kingine — HAKUNA KAZI INAYOPOTEA. "Anzisha Mashindano Rasmi" ndio
//  hatua pekee inayobadilisha draft kuwa tukio linaloendelea (pairings
//  za raundi ya kwanza zinatengenezwa wakati huo).
// ══════════════════════════════════════════════════════════════════════

const COLLECTION = 'liveEvents';

export async function fetchLiveEvents() {
  const snap = await getDocs(collection(db, COLLECTION));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
}

export async function fetchLiveEvent(id) {
  const s = await getDoc(doc(db, COLLECTION, id));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

// ── DRAFT — kuanzisha tukio KABLA ya washindani ────────────────────────
export async function createDraftLiveEvent({ title, category, pairingSystem, totalRounds, isBootstrap, author }) {
  const ref = await addDoc(collection(db, COLLECTION), {
    title, category, pairingSystem,
    totalRounds: totalRounds || null,
    participants: [],
    rounds: [],
    status: 'draft',
    winnerId: null, winnerName: null,
    isBootstrap: !!isBootstrap,
    refereeId: null, refereeName: null,
    helperIds: [], helperNames: [],
    createdBy: author.uid, createdByName: author.name,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateDraftSettings(eventId, patch) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  if (ev.status !== 'draft') throw new Error('Mipangilio inaweza kubadilishwa kabla ya kuanzisha mashindano pekee.');
  await updateDoc(doc(db, COLLECTION, eventId), { ...patch, updatedAt: serverTimestamp() });
}

// Mchezaji MMOJA — anahifadhiwa papo hapo, hakuna kusubiri hadi mwisho.
export async function addParticipantToDraft(eventId, participant) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  if (ev.status !== 'draft') throw new Error('Tukio hili limeshaanzishwa — tumia "Mchezaji Aliyechelewa" badala yake.');
  const exists = (ev.participants || []).some(p => p.id === participant.id);
  if (exists) return ev.participants;
  const participants = [...(ev.participants || []), participant];
  await updateDoc(doc(db, COLLECTION, eventId), { participants, updatedAt: serverTimestamp() });
  return participants;
}

// Wachezaji WENGI kwa mara moja — kwa "Bandika Orodha". Inaruka wale
// walioshapo badala ya kulalamika, ili orodha kubwa (mamia) iingie haraka.
export async function addManyParticipantsToDraft(eventId, newParticipants) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  if (ev.status !== 'draft') throw new Error('Tukio hili limeshaanzishwa.');
  const existingIds = new Set((ev.participants || []).map(p => p.id));
  const toAdd = newParticipants.filter(p => !existingIds.has(p.id));
  const participants = [...(ev.participants || []), ...toAdd];
  await updateDoc(doc(db, COLLECTION, eventId), { participants, updatedAt: serverTimestamp() });
  return { participants, added: toAdd.length, skipped: newParticipants.length - toAdd.length };
}

export async function removeParticipantFromDraft(eventId, participantId) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  if (ev.status !== 'draft') throw new Error('Mchezaji anaweza kuondolewa kabla ya kuanzisha mashindano pekee.');
  const participants = (ev.participants || []).filter(p => p.id !== participantId);
  await updateDoc(doc(db, COLLECTION, eventId), { participants, updatedAt: serverTimestamp() });
  return participants;
}

// ── KUANZISHA MASHINDANO RASMI — draft → ongoing ───────────────────────
// Hapa ndipo raundi ya kwanza (Round Robin / Knockout) inapopangwa, au
// Swiss inapoachwa tayari kwa raundi ya kwanza kupangwa na msimamizi.
export async function launchLiveEvent(eventId) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  if (ev.status !== 'draft') throw new Error('Tukio hili tayari limeanzishwa.');
  const participants = ev.participants || [];
  if (participants.length < 2) throw new Error('Ongeza angalau washindani 2 kabla ya kuanzisha.');

  const patch = { status: 'ongoing', launchedAt: serverTimestamp() };

  if (ev.pairingSystem === 'roundRobin') {
    const sched = roundRobinSchedule(participants);
    if (sched.error) throw new Error(sched.error);
    patch.rounds = sched.rounds.map((r, i) => ({ ...r, number: i + 1 }));
    patch.totalRounds = patch.rounds.length;
  } else if (ev.pairingSystem === 'knockout') {
    const withRatings = [];
    for (const p of participants) {
      const player = await fetchPlayer(p.id);
      withRatings.push({ ...p, _rating: ratingOf(player, ev.category) ?? 0 });
    }
    withRatings.sort((a, b) => b._rating - a._rating);
    const bracket = knockoutBracket(withRatings);
    if (bracket.error) throw new Error(bracket.error);
    patch.rounds = bracket.rounds;
    patch.bracketSize = bracket.bracketSize;
  }
  // Swiss: raundi ya kwanza inapangwa na msimamizi baada ya kuanzisha

  await updateDoc(doc(db, COLLECTION, eventId), patch);
  return patch;
}

// ── WASAIDIZI (staff wengi wanaweza kusaidia kuendesha tukio moja) ─────
// Admin ndiye kiongozi na mwenye uamuzi wa mwisho daima — wasaidizi ni
// nyongeza ya kufanya kazi iende haraka kwenye mashindano makubwa (mf.
// wanafunzi 400), si mbadala wa admin.
export async function addHelper(eventId, uid, name) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  if ((ev.helperIds || []).includes(uid)) return;
  await updateDoc(doc(db, COLLECTION, eventId), {
    helperIds: [...(ev.helperIds || []), uid],
    helperNames: [...(ev.helperNames || []), name],
    updatedAt: serverTimestamp(),
  });
}

export async function removeHelper(eventId, uid) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  const idx = (ev.helperIds || []).indexOf(uid);
  if (idx === -1) return;
  const helperIds = (ev.helperIds || []).filter((_, i) => i !== idx);
  const helperNames = (ev.helperNames || []).filter((_, i) => i !== idx);
  await updateDoc(doc(db, COLLECTION, eventId), { helperIds, helperNames, updatedAt: serverTimestamp() });
}

// Mchezaji aliyechelewa — baada ya mashindano kuanzishwa (Swiss pekee,
// kama ilivyokuwa awali, kwa sababu Round Robin/Knockout zimeshapangwa).
export async function addParticipantLive(eventId, participant) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  if (ev.pairingSystem !== 'swiss') {
    throw new Error('Kuongeza mchezaji katikati kunaruhusiwa kwa Swiss pekee — Round Robin na Knockout zimeshapangwa.');
  }
  const exists = (ev.participants || []).some(p => p.id === participant.id);
  if (exists) throw new Error('Mchezaji huyu tayari yupo kwenye tukio hili.');
  const participants = [...(ev.participants || []), participant];
  await updateDoc(doc(db, COLLECTION, eventId), { participants, updatedAt: serverTimestamp() });
  return participants;
}

// ── Standings (Swiss / Round Robin) — sawa na mtindo wa Ofisini ────────
export function computeLiveStandings(event) {
  const totals = {};
  (event.participants || []).forEach(p => {
    totals[p.id] = { id: p.id, name: p.name, points: 0, wins: 0, draws: 0, losses: 0, games: 0, whiteCount: 0, blackCount: 0, byes: 0 };
  });
  (event.rounds || []).forEach(round => {
    (round.pairings || []).forEach(p => {
      if (p.isBye) return; // bye ya knockout haitoi pointi za mfumo wa points
      if (!p.result || !totals[p.whiteId] || !totals[p.blackId]) return;
      totals[p.whiteId].games++; totals[p.blackId].games++;
      totals[p.whiteId].whiteCount++; totals[p.blackId].blackCount++;
      if (p.result === '1-0') { totals[p.whiteId].points += 1; totals[p.whiteId].wins++; totals[p.blackId].losses++; }
      else if (p.result === '0-1') { totals[p.blackId].points += 1; totals[p.blackId].wins++; totals[p.whiteId].losses++; }
      else if (p.result === '½-½') { totals[p.whiteId].points += 0.5; totals[p.whiteId].draws++; totals[p.blackId].points += 0.5; totals[p.blackId].draws++; }
    });
    if (round.byeId && totals[round.byeId]) { totals[round.byeId].points += 1; totals[round.byeId].byes++; }
  });
  return Object.values(totals).sort((a, b) => b.points - a.points || b.wins - a.wins || a.name.localeCompare(b.name));
}

// ── Raundi inayofuata (Swiss) ────────────────────────────────────────────
export function generateNextSwissRound(event) {
  const standings = computeLiveStandings(event);
  const playedPairs = new Set();
  const byeHistory = new Set();
  (event.rounds || []).forEach(r => {
    (r.pairings || []).forEach(p => { if (!p.isBye) playedPairs.add([p.whiteId, p.blackId].sort().join('__')); });
    if (r.byeId) byeHistory.add(r.byeId);
  });
  return swissPairing({ standings, playedPairs, byeHistory });
}

export async function saveRound(eventId, round, roundNumber) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  let rounds;
  if (roundNumber) {
    rounds = (ev.rounds || []).map(r => r.number === roundNumber ? { ...round, number: roundNumber } : r);
  } else {
    rounds = [...(ev.rounds || []), { ...round, number: (ev.rounds?.length || 0) + 1 }];
  }
  await updateDoc(doc(db, COLLECTION, eventId), { rounds, updatedAt: serverTimestamp() });
  return rounds;
}

// ── Knockout: pandisha washindi kwenda raundi inayofuata ───────────────
export async function advanceKnockout(eventId) {
  const ev = await fetchLiveEvent(eventId);
  if (!ev) throw new Error('Tukio halikupatikana.');
  const last = ev.rounds[ev.rounds.length - 1];
  if (last.pairings.length === 1 && !last.pairings[0].isBye) {
    throw new Error('Hii ilikuwa fainali — hakuna raundi nyingine.');
  }
  const next = knockoutNextRound(last.pairings);
  if (next.error) throw new Error(next.error);
  const rounds = [...ev.rounds, { number: ev.rounds.length + 1, pairings: next.pairings }];
  await updateDoc(doc(db, COLLECTION, eventId), { rounds, updatedAt: serverTimestamp() });
  return rounds;
}

export async function declareLiveWinner(eventId, winnerId, winnerName) {
  await updateDoc(doc(db, COLLECTION, eventId), {
    status: 'finished', winnerId, winnerName, finishedAt: serverTimestamp(),
  });
}

export async function deleteLiveEvent(id) {
  await deleteDoc(doc(db, COLLECTION, id));
}

// ══════════════════════════════════════════════════════════════════════
//  MAKADIRIO YA RATING KWA WACHEZAJI WOTE — kwa onyesho la moja kwa moja
//  wakati tukio linaendelea. Halibadilishi chochote Firestore.
// ══════════════════════════════════════════════════════════════════════
export async function computeLiveProjections(event) {
  const players = {};
  for (const p of event.participants) {
    players[p.id] = await fetchPlayer(p.id);
  }

  const gamesByPlayer = {};
  (event.rounds || []).forEach(round => {
    (round.pairings || []).forEach(p => {
      if (p.isBye || !p.result) return;
      const wRating = ratingOf(players[p.whiteId], event.category);
      const bRating = ratingOf(players[p.blackId], event.category);
      const wScore = p.result === '1-0' ? 1 : p.result === '½-½' ? 0.5 : 0;
      const bScore = 1 - wScore;
      (gamesByPlayer[p.whiteId] = gamesByPlayer[p.whiteId] || []).push({ opponentRating: bRating ?? 1200, score: wScore });
      (gamesByPlayer[p.blackId] = gamesByPlayer[p.blackId] || []).push({ opponentRating: wRating ?? 1200, score: bScore });
    });
  });

  const out = {};
  event.participants.forEach(p => {
    const player = players[p.id];
    if (!player) { out[p.id] = null; return; }
    out[p.id] = liveProjection({ player, category: event.category, liveGames: gamesByPlayer[p.id] || [] });
  });
  return out;
}

// ══════════════════════════════════════════════════════════════════════
//  KUMALIZA TUKIO — kuhamishia kwenye mfumo rasmi (ratedEvents + ratedGames)
//  Baada ya hapa, tukio linaenda kwenye mzunguko wa kawaida wa
//  "Hakiki → Chapisha" kwenye Usimamizi wa IFR, bila kubadilishwa.
// ══════════════════════════════════════════════════════════════════════
export async function finalizeLiveEvent(event, { arbiterName, venue, author }) {
  const eventId = await saveEvent({
    name: event.title,
    type: 'mashindano',
    category: event.category,
    startDate: new Date().toISOString().split('T')[0],
    periodKey: periodKeyOf(new Date()),
    arbiterName: arbiterName || event.createdByName,
    venue: venue || '',
    bootstrap: !!event.isBootstrap,
    liveEventId: event.id,
    pairingSystem: event.pairingSystem,
  });

  const playerCache = {};
  async function ratingBefore(pid) {
    if (!(pid in playerCache)) playerCache[pid] = await fetchPlayer(pid);
    return ratingOf(playerCache[pid], event.category);
  }

  let gamesCount = 0;
  for (const round of event.rounds) {
    for (const p of round.pairings) {
      if (p.isBye || !p.result || !p.blackId) continue; // bye na pairings zisizo na matokeo hazihesabiki
      await addGame({
        eventId,
        eventName: event.title,
        periodKey: periodKeyOf(new Date()),
        category: event.category,
        whiteId: p.whiteId, whiteName: p.whiteName,
        blackId: p.blackId, blackName: p.blackName,
        whiteRatingBefore: await ratingBefore(p.whiteId),
        blackRatingBefore: await ratingBefore(p.blackId),
        result: p.result,
        rated: false,
      });
      gamesCount++;
    }
  }

  await updateDoc(doc(db, COLLECTION, event.id), {
    finalizedEventId: eventId, finalizedAt: serverTimestamp(), finalizedBy: author?.name || '',
  });

  return { eventId, gamesCount };
}

export { categoryInfo };
